import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  SubController,
} from '@hit/amorphic-types';
import { PiiLevelEnum } from './logger/LoggerSingleton';
import { AuthenticatingController } from './AuthenticatingController';
import { Customer } from './customer/Customer';
import { UICategory, UILoggerSingleton } from './logger/UILoggerSingleton';
import { Utils } from './Utils';

declare var ActiveXObject: { new (s: string) };
declare var $;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type MessageMap = {
  message: string;
  minlength?: number | string;
  maxlength?: number | string;
  min?: number | string;
  max?: number | string;
};

@supertypeClass({ toClient: true, toServer: true })
export abstract class BaseController extends AuthenticatingController {
  @property({ isLocal: true, persist: false })
  router;

  @property({ isLocal: true, persist: false })
  route;

  @property({ isLocal: true, persist: false })
  errorCount: number;

  @property()
  error;

  @property({ getType: () => Customer })
  customer: Customer;

  @property()
  value;

  constructor() {
    super();
  }

  init(): this {
    return this;
  }

  isPassword(): void {
    // From amorphic-userman: Password must be 8-30 characters with at least one (uppercase and lowercase) letter, number, and special char
    this.mustMatch(/^.{8,30}$/, 'password_composition');
    this.mustMatch('[0-9]', 'password_composition');
    this.mustMatch('[a-z]', 'password_composition');
    this.mustMatch('[A-Z]', 'password_composition');
    this.mustMatch('[-?!%$#&+_*@]', 'password_composition');
  }

  isName(): void {
    // TODO This supports commas; why the separate function below? (HL-8992)
    this.mustMatch(/^[a-z ,.'-]+$/i, 'name2');
  }

  isNameSupportComma(): void {
    // TODO Why does this allow numbers? (HL-8992)
    this.mustNotMatch("[^0-9A-Za-z '-,]", 'name');
  }

  isText(): void {
    this.mustNotMatch("[^\\-0-9A-Za-z !@#$%^&*()_+={}|][\":;'<>?',.]", 'text');
  }

  isEmail(): void {
    this.mustMatch(/^([\w.%+\-]+)@([\w\-]+\.)+([\w]{2,})$/i, 'email');
  }

  isNumeric(): void {
    this.mustNotMatch('[^0-9]', 'Please enter a number');
  }

  isPercent(): void {
    this.isNumeric();

    if (this.value && (this.value < 1 || this.value > 100)) {
      this.throwMessage({
        message: 'percent',
      });
    }
  }

  isGeoName(): void {
    this.mustMatch(/^([a-zA-Z\u0080-\u024F]+(?:. |-| |'))*[a-zA-Z\u0080-\u024F]*$/, 'Invalid Name');
  }

  isAlphaNumeric(): void {
    this.mustNotMatch('[^0-9A-Za-z]', 'alphanumeric');
  }

  isPhone(): void {
    // TODO Does not properly test length/format (HL-8992)
    this.mustNotMatch('[^0-9 ()-]', 'phone');
  }

  isSSN(): void {
    this.mustMatch(/^\d{3}-\d{2}-\d{4}$/, 'ssn');
  }

  isTIN(): void {
    this.mustMatch(/^\d{3}-\d{2}-\d{4}$/, 'tin');
  }

  isEIN(): void {
    this.mustMatch(/^\d{2}-\d{7}$/, 'ein');
  }

  isEINSSN(): void {
    this.mustMatch(/(^\d{2}-\d{7}$)|(^\d{3}-\d{2}-\d{4}$)/, 'einssn');
  }

  isLast4SSN(): boolean {
    const regex = '[0-9]{4}';

    if (this.value !== null && this.value.length > 0 && (this.value.length !== 4 || !String(this.value).match(regex))) {
      this.throwMessage({
        message: 'Incorrect Format',
      });
    }

    // TODO Always returns true; seems inconsistent with other validators that return undefined? (HL-8992)
    return true;
  }

  throwMessage(messageMap: MessageMap | string) {
    // Middle layer to customize error message
    throw messageMap;
  }

  isTaxID(): void {
    // TODO Always throws error (HL-8992)
    //tslint:disable-next-line
    this.mustMatch('[0-9]{3}-[0-9]{2}-[0-9]{4}', 'taxid');
    this.mustMatch('[0-9]{2}-[0-9]{6}', 'taxid');
  }

  isZip5(): void {
    // TODO Needs to specify start and end of regex tested string (^$) (HL-8992)
    this.mustMatch('[0-9]{5}', 'zip5');
  }

  isZip5OrBlank(): void {
    if (this.value) {
      this.mustMatch('[0-9]{5}', 'zip5');
    }
  }

  isValidZipcode(zipcodeToValidate?: string): boolean {
    return Utils.isValidZipcode(zipcodeToValidate || this.value);
  }

  validateZipcode(): void {
    if (!this.isValidZipcode()) {
      this.throwMessage({
        message: 'invalid-zip',
      });
    }
  }

  parseZipcode(): string {
    if (!this.value) {
      return '';
    }

    return this.value.replace(/-/g, '');
  }

  formatZipcode(): string {
    const zipcode = this.value;

    if (!zipcode) {
      return '';
    }

    if (zipcode.length > 5) {
      return zipcode.slice(0, 5) + '-' + zipcode.slice(5);
    } else {
      return zipcode;
    }
  }

  notEmpty(): void {
    const valueEmpty = this.value === null || this.value === undefined || this.value.length === 0;

    if (valueEmpty) {
      this.throwMessage({
        message: 'required',
      });
    }
  }

  greaterThanZero(): void {
    // TODO Currently passes with non-numeric values (HL-8992)
    if (this.value === null || this.value <= 0) {
      throw {
        code: 'error',
        message: 'Please enter a number greater than zero',
      };
    }
  }

  isWithin(min: number, max: number): void {
    // THIS FUNCTION IS INCLUSIVE!
    if (this.value === null || this.value.length === 0) {
      return;
    }

    const f = parseFloat(this.value);

    if (isNaN(f)) {
      this.throwMessage({
        message: 'Please enter a number',
      });
    }

    if (this.value < min) {
      this.throwMessage({
        message: 'min',
        min: min,
      });
    }

    if (this.value > max) {
      this.throwMessage({
        message: 'max',
        max: max,
      });
    }
  }

  isMinLength(len: number): void {
    if (this.isEmpty() || this.value.length < len) {
      this.throwMessage({
        message: 'minlength',
        minlength: len,
      });
    }
  }

  isNotEmptyAndMinLength(len: number): void {
    if (!this.isEmpty() && this.value.length < len) {
      this.throwMessage({
        message: 'minlength',
        minlength: len,
      });
    }
  }

  isMaxLength(len: number): void {
    if (this.isEmpty() || this.value.length > len) {
      this.throwMessage({
        message: 'maxlength',
        maxlength: len,
      });
    }
  }

  isEmpty(): boolean {
    return this.value === null || this.value.length === 0;
  }

  isDOBWithin(min: number, max: number): void {
    if (this.value === null || this.value.length === 0) {
      return;
    }

    const age = this.getAge(this.value);

    if (age > max || age < min) {
      this.throwMessage({
        message: 'invaliddob',
        min: min,
        max: max,
      });
    }
  }

  isPositiveNumber(): void {
    if (this.value === null || this.value.length === 0) {
      return;
    }

    const f = parseFloat(this.value);

    if (isNaN(f)) {
      this.throwMessage({
        message: 'Please enter a number',
      });
    }

    if (this.value < 0) {
      this.throwMessage({
        message: 'Value cannot be negative',
      });
    }
  }

  pastDate(): boolean | null {
    if (!this.value) {
      return null;
    }

    // Has to be a past date
    const date = new Date(this.value),
      now = new Date();

    if (date > new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))) {
      this.throwMessage({
        message: 'pastdate',
      });
    }

    return true;
  }

  validZeroOrVal(val: string): boolean {
    if (!this.value || this.value === val) {
      return true;
    } else {
      this.throwMessage({
        message: 'maxpoints',
        max: val,
      });
    }
  }

  isLegalAdult(min: number): void {
    if (this.value === null || this.value.length === 0) {
      return null;
    }

    const age = this.getAge(this.value);

    if (age < min) {
      this.throwMessage({
        message: 'The owner must be at least 18 years of age',
      });
    }
  }

  getAge(birthdate: Date): number {
    //birthdate is stored in UTC, but we want to compare to today in local time for the user
    const today = new Date();
    const ageOnThisYearsBirthday = today.getFullYear() - birthdate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthdate.getUTCMonth();
    const dayDiff = today.getDate() - birthdate.getUTCDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      return ageOnThisYearsBirthday - 1;
    }

    return ageOnThisYearsBirthday;
  }

  parseCurrency(): number {
    if (!this.value) {
      return 0;
    }

    let n = this.value;

    n = n.replace(/k/i, '000');
    n = n.replace(/[^0-9.\-]/g, '');

    const f = parseFloat(n);

    if (isNaN(f)) {
      this.throwMessage({
        message: 'Please enter a number',
      });
    }

    const result = Math.floor(f * 100 + 0.5) / 100;

    return result;
  }

  parsePercent(): number {
    if (!this.value) {
      return 0;
    }

    let n = this.value;

    n = n.replace(/[^0-9.\-]/g, '');

    const f = parseFloat(n);

    if (isNaN(f)) {
      this.throwMessage({
        message: 'number',
      });
    }

    const result = f / 100;

    return result;
  }

  parseNumber(): number {
    if (!this.value) {
      return null;
    }

    let n = this.value;

    n = n.replace(/k/i, '000');
    n = n.replace(/[^0-9.\-]/g, '');

    const result = parseFloat(n);

    if (isNaN(result)) {
      this.throwMessage({
        message: 'Please enter a number',
      });
    }

    return result;
  }

  parseInteger(): number {
    if (!this.value) {
      return null;
    }

    let n = this.value;

    n = n.replace(/k/i, '000');
    n = n.replace(/[^0-9.\-]/g, '');

    const result = parseInt(n);

    if (isNaN(result)) {
      this.throwMessage({
        message: 'Please enter a number',
      });
    }

    return result;
  }

  parseUTCDate(): Date {
    if (this.value === null || this.value.length === 0) {
      return null;
    }

    if (!this.isDateValid(this.value)) {
      this.throwMessage({
        message: 'date',
      });
    }

    const dtParts = this.value.split('/');
    const parsed = Date.UTC(dtParts[2], Number(dtParts[0]) - 1, dtParts[1]);

    if (isNaN(parsed)) {
      this.throwMessage({
        message: 'date',
      });
    }

    return new Date(parsed);
  }

  parseUTCShortDate(): Date {
    if (this.value === null || this.value.length === 0) {
      return null;
    }

    if (!this.isShortDateValid(this.value)) {
      this.throwMessage({
        message: 'shortdate',
      });
    }

    // Add a day of 01 and convert to Date
    const tokens = this.value.split('/');
    const parsed = Date.UTC(tokens[1], Number(tokens[0]) - 1, 1);

    if (isNaN(parsed)) {
      this.throwMessage({
        message: 'shortdate',
      });
    }

    // Has to be a past date
    const date = new Date(parsed);
    const now = new Date();

    if (date > new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))) {
      this.throwMessage({
        message: 'shortdate',
      });
    }

    return date;
  }

  parseUTCFutureShortDate(): Date {
    if (this.value === null || this.value.length === 0) {
      return null;
    }

    if (!this.isShortDateValid(this.value)) {
      this.throwMessage({
        message: 'futureshortdate',
      });
    }

    // Add a day of 01 and convert to Date
    const tokens = this.value.split('/');
    const parsed = Date.UTC(tokens[1], Number(tokens[0]) - 1, 1);

    if (isNaN(parsed)) {
      this.throwMessage({
        message: 'futureshortdate',
      });
    }

    // Has to be a futue date
    const date = new Date(parsed),
      now = new Date();

    if (date < new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, now.getDate()))) {
      this.throwMessage({
        message: 'futureshortdate',
      });
    }

    return date;
  }

  parseUTCFutureDate(): Date {
    // Limit the date to next 100 yrs
    const dt = this.parseUTCDate(),
      now = new Date();

    if (dt < new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))) {
      this.throwMessage({
        message: 'futuredate',
      });
    }

    return dt;
  }

  parseDOB(): Date {
    return this.parseUTCDate();
  }

  // allowing boolean so it can be subclassed to include an early positive result
  isDateValid(date: string): boolean | RegExpMatchArray {
    const dateRegex = /^([0]?[1-9]|[1][0-2])[.\/-]([0]?[1-9]|[1|2][0-9]|[3][0|1])[.\/-]([0-9]{4}|[0-9]{2})$/;

    return date.match(dateRegex);
  }

  // allowing boolen so it can be subclassed to include an early positive result
  isShortDateValid(date: string): boolean | RegExpMatchArray {
    // mm/yyyy
    const dateRegex = /^([0]?[1-9]|[1][0-2])[.\/-]([0-9]{4}|[0-9]{2})$/;

    return date.match(dateRegex);
  }

  parseRemoveSpace(): string {
    return this.value ? this.value.trim() : this.value;
  }

  formatText(value: string): string {
    value = value || this.value;

    if (value === null || typeof value === 'undefined') {
      return '';
    }

    return String(value).replace(/<.*>/g, ' ').trim();
  }

  formatToLowerCase(value: string): string {
    value = value || this.value;

    if (value === null || typeof value === 'undefined') {
      return '';
    }

    return String(value).trim().toLowerCase();
  }

  formatUTCDate(value: Date): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    const date = value;

    return date.getUTCMonth() + 1 + '/' + date.getUTCDate() + '/' + date.getUTCFullYear();
  }

  formatDate(value: Date): string {
    return Utils.formatDate(value || this.value);
  }

  formatUTCShortDate(value: Date): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    const date = value;

    // Ignore date
    return date.getUTCMonth() + 1 + '/' + date.getUTCFullYear();
  }

  formatDateTime(value: Date): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    const date = new Date(value);

    return (
      date.getMonth() +
      1 +
      '/' +
      date.getDate() +
      '/' +
      date.getFullYear() +
      ' ' +
      date.toTimeString().replace(/ .*/, '')
    );
  }

  formatDateNatural(value: Date): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    let monthDay;
    const today = new Date();
    const yesterday = new Date();

    yesterday.setDate(yesterday.getDate() - 1);

    if (today.toDateString() === value.toDateString()) {
      monthDay = 'Today';
    } else if (yesterday.toDateString() === value.toDateString()) {
      monthDay = 'Yesterday';
    } else {
      monthDay = MONTHS[value.getMonth()] + ' ' + value.getDate();
    }

    const year = this.getAge(this.value) > 0 ? ' ' + value.getFullYear().toString() : '';

    // year is blank unless >= 1 year ago
    return monthDay + year;
  }

  formatTime12Hour(value: Date): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    return value.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDateTimeNatural(value: Date): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    return this.formatDateNatural(value) + ', ' + this.formatTime12Hour(value);
  }

  formatPercent(value: number): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    return Math.round(value * 100) + '%';
  }

  formatPercentTwoDecimalNoPercentSign(value: number): string | number {
    value = value || this.value;

    if (!value) {
      return '';
    }

    return Math.round(value * 1e4) / 100;
  }

  formatDollar(prependSymbol: string, value: number): string {
    value = value || this.value;

    if (value === null || value === 0) {
      return '';
    }

    return this.formatCurrencyZero(prependSymbol || '$', value);
  }

  formatDollarZero(prependSymbol: string, value: number): string {
    value = value || this.value;

    if (value === null) {
      return '';
    }

    return this.formatCurrencyZero(prependSymbol || '$', value);
  }

  formatDollarRounded(prependSymbol: string, value: number): string {
    value = value || this.value;

    if (value === null || value === 0) {
      return '';
    }

    return this.formatCurrencyZero(prependSymbol || '$', Math.round(value));
  }

  formatDollarDoNotRound(prependSymbol: string, value: number) {
    value = value || this.value;

    if (value === null || value === 0) {
      return '';
    }

    return this.formatCurrencyInternal(value, prependSymbol || '$', true);
  }

  formatCurrency(prependSymbol: string, value: number): string {
    value = value || this.value;

    if (value === null || value === 0) {
      return '';
    }

    return this.formatCurrencyZero(prependSymbol || '$', value);
  }

  formatCurrencyZero(prependSymbol: string, value: number): string {
    value = value || this.value;

    if (value === null) {
      return '';
    }

    return this.formatCurrencyInternal(value, prependSymbol || '$');
  }

  formatCurrencyCents(prependSymbol: string, value: number): string {
    value = value || this.value;

    if (value === null || value === 0) {
      return '';
    }

    return this.formatCurrencyInternal(value, prependSymbol || '$', true);
  }

  formatCurrencyInternal(value: number, prependSymbol?: string, noround?: boolean): string {
    prependSymbol = prependSymbol || '';

    let n = String(noround ? value : Math.round(value));
    n = n.replace(/\.([0-9])$/, '.$10');

    const p = value < 0 ? ['-', ''] : ['', ''];

    return p[0] + prependSymbol + this.addCommas(n.replace(/-/, '')) + p[1];
  }

  formatFaceAmount(value?: number): string {
    value = value || this.value;
    if (!value) {
      return '';
    }
    if (value < 1e6) {
      return '$' + Math.round(value / 1000) + 'k';
    }
    return '$' + Math.round(value / 1000) / 1000 + 'm';
  }

  formatNumber(value: string): string {
    value = value || this.value;

    if (value === null) {
      return '';
    }

    return this.addCommas(value);
  }

  parseTelephone(): string {
    if (!this.value) {
      return null;
    }

    let str = this.value.replace(/[^0-9]/g, '');

    if (!str) {
      this.throwMessage({
        message: 'phone',
      });

      return null;
    }

    if (str.length > 10) {
      str = str.replace(/^1/, '');
    }

    const num = str.substr(0, 10);
    const ext = str.length > 10 ? ' ' + str.substr(10) : '';

    if (num.length < 10) {
      this.throwMessage({
        message: 'phone',
      });
    }

    if (ext.length > 11) {
      this.throwMessage({
        message: 'phoneMax',
      });
    }

    return num + ext;
  }

  parseCapitalizeName(): string {
    if (this.value === null || typeof this.value === 'undefined') {
      return '';
    }

    return this.value.trim().replace(/^(.)/, function ($1) {
      return $1.toUpperCase();
    });
  }

  emailLowerCase(): string {
    return this.value ? this.value.trim().toLowerCase() : this.value;
  }

  formatTelephone(value: string): string {
    return Utils.formatTelephone(value || this.value);
  }

  parseGeoName(): string {
    return this.parseText();
  }

  formatSSN(value: string): string {
    value = value || this.value;

    if (!value || value.length < 9) {
      return '';
    }

    return value.substr(0, 3) + '-' + value.substr(3, 2) + '-' + value.substr(5, 4);
  }

  formatEIN(value: string): string {
    value = value || this.value;

    if (!value || value.length < 9) {
      return '';
    }

    return value.substr(0, 2) + '-' + value.substr(2, 7);
  }

  parseEINSSN(): string {
    if (!this.value) {
      return null;
    }

    let isSSN = this.value.substr(3, 1) === '-';
    const str = this.value.replace(/[^0-9]/g, '');

    if (!str) {
      return null;
    }

    if (str.length < 9) {
      this.throwMessage({
        message: 'ssnein',
      });
    }

    isSSN = this.value.indexOf('-') < 0;

    // If no '-', format as SSN
    return isSSN ? this.formatSSN(str) : this.formatEIN(str);
  }

  parseSSN(): string | null {
    if (!this.value) {
      return null;
    }

    const str = this.value.replace(/[^0-9]/g, '');

    if (!str) {
      return null;
    }

    if (str.length < 9) {
      this.throwMessage({
        message: 'ssn',
      });
    }

    return this.formatSSN(str);
  }

  parseTIN(): string | null {
    if (!this.value) {
      return null;
    }

    const str = this.value.replace(/[^0-9]/g, '');

    if (!str) {
      return null;
    }

    if (str.length < 9) {
      this.throwMessage({
        message: 'tin',
      });
    }

    return this.formatSSN(str);
  }

  parseEIN(): string | null {
    if (!this.value) {
      return null;
    }

    const str = this.value.replace(/[^0-9]/g, '');

    if (!str) {
      return null;
    }

    if (str.length < 9) {
      this.throwMessage({
        message: 'ssn',
      });
    }

    return this.formatEIN(str);
  }

  parseText(): string {
    if (!this.value) {
      return '';
    }

    return this.value.trim();
  }

  parseLocalDate(): Date {
    if (this.value === null || this.value.length === 0) {
      return null;
    }

    const parsed = Date.parse(this.value);

    if (isNaN(parsed)) {
      this.throwMessage({
        message: 'date',
      });
    }

    return new Date(parsed);
  }

  formatLocalDate(value: Date): string {
    value = value || this.value;

    if (!value) {
      return '';
    }

    const date = value;

    return date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear();
  }

  clientInit(_unusedParam?: string): void {
    if (typeof this['__ver'] !== 'undefined') {
      this.setIncludeURLSuffix('?ver=' + this['__ver']);
    }

    this.attr(
      '.currency',
      {
        format: this.formatDollar,
      },
      null
    );

    this.attr(
      '.spin',
      {
        min: '{prop.min}',
        max: '{prop.max}',
      },
      null
    );

    this.rule('text', {
      maxlength: '{prop.length}',
      validate: this.isText,
      format: this.formatText,
      parse: this.parseText,
    });

    this.rule('numeric', {
      parse: this.parseNumber,
      format: this.formatText,
    });

    this.rule('numericPositive', {
      parse: this.parseNumber,
      format: this.formatText,
      validate: this.isPositiveNumber,
    });

    this.rule('name', {
      maxlength: '{prop.length}',
      validate: this.isName,
      parse: this.parseCapitalizeName,
    });

    this.rule('nameSupportComma', {
      maxlength: '{prop.length}',
      validate: this.isNameSupportComma,
    });

    this.rule('email', {
      parse: this.parseRemoveSpace,
      validate: this.isEmail,
    });

    this.rule('currency', {
      format: this.formatDollar,
      parse: this.parseCurrency,
    });

    this.rule('currencyZero', {
      format: this.formatDollarZero,
      parse: this.parseCurrency,
    });

    this.rule('currencycents', {
      format: this.formatCurrencyCents,
      parse: this.parseCurrency,
    });

    this.rule('localdate', {
      format: this.formatLocalDate,
      parse: this.parseLocalDate,
    });

    this.rule('date', {
      format: this.formatUTCDate,
      parse: this.parseUTCDate,
    });

    this.rule('shortdate', {
      format: this.formatUTCShortDate,
      parse: this.parseUTCShortDate,
    });

    this.rule('futureshortdate', {
      format: this.formatUTCShortDate,
      parse: this.parseUTCFutureShortDate,
    });

    this.rule('futuredate', {
      format: this.formatUTCDate,
      parse: this.parseUTCFutureDate,
    });

    this.rule('datetime', {
      format: this.formatDateTime,
      parse: this.parseUTCDate,
    });

    this.rule('DOB', {
      format: this.formatUTCDate,
      parse: this.parseDOB,
    });

    this.rule('SSN', {
      validate: this.isSSN,
    });

    this.rule('TIN', {
      validate: this.isTIN,
    });

    this.rule('EIN', {
      validate: this.isEIN,
    });

    this.rule('EINSSN', {
      validate: this.isEINSSN,
    });

    this.rule('taxid', {
      validate: this.isTaxID,
    });

    this.rule('phone', {
      validate: this.isPhone,
    });

    this.rule('required', {
      validate: this.notEmpty,
    });

    this.rule('percent', {
      validate: this.isPercent,
      format: this.formatPercent,
    });

    this.rule('zip5', {
      validate: this.isZip5,
    });

    this.rule('zipcode', {
      validate: this.validateZipcode,
      parse: this.parseZipcode,
      format: this.formatZipcode,
    });

    this.rule('telephone', {
      parse: this.parseTelephone,
      format: this.formatTelephone,
    });

    this.rule('numericInt', {
      parse: this.parseInteger,
      format: this.formatText,
    });

    this.rule('numericstring', {
      validate: this.isNumeric,
      format: this.formatText,
    });

    this.rule('geoName', {
      maxlength: '{prop.length}',
      validate: this.isGeoName,
      parse: this.parseGeoName,
    });

    this.rule('emailLowerCase', {
      parse: this.emailLowerCase,
    });
  }

  addCommas(nStr: string): string {
    nStr = String(nStr);
    const x = nStr.split('.');
    let x1 = x[0];
    const x2 = x.length > 1 ? '.' + x[1] : '';
    const rgx = /(\d+)(\d{3})/;

    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1' + ',' + '$2');
    }

    return x1 + x2;
  }

  mustNotMatch(regex: RegExp | string, error: string): void {
    if (this.value !== null && this.value.length > 0 && String(this.value).match(regex)) {
      const toThrow = error
        ? {
            message: error,
          }
        : ' Incorrect Format';

      this.throwMessage(toThrow);
    }
  }

  mustMatch(regex: RegExp | string, error: string): void {
    if (this.value !== null && this.value.length > 0 && !String(this.value).match(regex)) {
      const toThrow = error
        ? {
            message: error,
          }
        : ' Incorrect Format';

      this.throwMessage(toThrow);
    }
  }

  serverLog(text: string): void {
    UILoggerSingleton.getInstance().info(
      {
        module: 'baseController',
        function: 'serverLog',
        category: UICategory.request,
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
      },
      text
    );

    if (this.errorCount < 3) {
      this.xhr('/log', 'text/plain', text, this, function () {});
    }
  }

  getModalLeft(dialogWidth: number): number {
    let element;
    let attr = 'inner';

    if (!('innerWidth' in window)) {
      attr = 'client';
      element = document.documentElement || document.body;
    } else {
      element = window;
    }

    return Math.round(element[attr + 'Width'] / 2 - dialogWidth / 2);
  }

  getModalTop(dialogHeight: number): number {
    let element;
    let attr = 'inner';

    if (!('innerHeight' in window)) {
      attr = 'client';
      element = document.documentElement || document.body;
    } else {
      element = window;
    }

    return Math.round(element[attr + 'Height'] / 2 - dialogHeight / 2);
  }

  clientExpire(): boolean {
    return false;
  }

  xhr(url, contentType, data, callbackobj, callbackfn, errcallbackobj?, errcallbackfn?) {
    const request = this.getxhr();

    request.open(data ? 'PUT' : 'GET', url, true);
    request.setRequestHeader('Content-type', contentType);

    const self = this;

    request.onreadystatechange = function () {
      if (request.readyState !== 4) {
        return;
      }

      let status, statusText;

      try {
        status = request.status;
        statusText = request.statusText;
      } catch (e) {
        status = 666;
        statusText = 'unknown';
      }

      if (status === 200) {
        self.errorCount = 0;
        callbackfn.call(callbackobj, request);
      } else {
        ++self.errorCount;

        const error =
          'Server request failed\nurl: ' + url + '\nstatus: ' + statusText + '\nmessage:' + request.responseText;

        if (errcallbackfn) {
          errcallbackfn.call(errcallbackobj, request, error);
        } else {
          if (self.errorCount < 3) {
            alert(error);
          }
        }
      }
    };

    request.send(data);
  }

  getxhr(): XMLHttpRequest {
    return new XMLHttpRequest();
  }

  loadScript(src: string, then: () => void): void {
    const head = document.getElementsByTagName('head')[0];
    const script = document.createElement('script');
    const thenFunction = then;
    const self = this;

    script.type = 'text/javascript';
    script.src = src;

    if (then) {
      if (document.all) {
        script['onreadystatechange'] = function () {
          if (script['readyState'] === 'complete') {
            script['onreadystatechange'] = '';
          } else if (script['readyState'] === 'loaded') {
            script['onreadystatechange'] = '';
          }

          thenFunction.call(self);
        };
      } else {
        script.onload = function () {
          thenFunction.call(self);
        };
      }
    }

    head.appendChild(script);
  }
}
